/** Entprellte Ortssuche (ohne React, damit die Logik testbar bleibt). */

import { GeoOption } from '../../campaign-wizard.types';
import { GEO_DEBOUNCE_MS, GeoErrorKind, geoErrorKind, geoQueryReady } from './candidates-logic';

export type GeoLookup = (accountId: string, query: string, signal: AbortSignal) => Promise<GeoOption[]>;

export interface GeoSearchHandlers {
  onLoading: (loading: boolean) => void;
  onOptions: (options: GeoOption[]) => void;
  onError: (kind: GeoErrorKind | null) => void;
}

/**
 * Startet eine Suche nach `query` und gibt eine Funktion zum Abbrechen zurück (neuer Tastendruck, Schließen).
 * Ohne Konto oder unter zwei Zeichen wird LinkedIn nicht gefragt und die Liste geleert; sonst wird nach `delay` ms
 * gesucht, eine ältere Antwort überschreibt nie eine neuere. Fehler (z. B. abgelaufene Sitzung) leeren die Liste und
 * werden als `session` / `failed` gemeldet.
 */
export const startGeoSearch = (
  accountId: string,
  query: string,
  lookup: GeoLookup,
  handlers: GeoSearchHandlers,
  delay = GEO_DEBOUNCE_MS
): (() => void) => {
  if (!accountId || !geoQueryReady(query)) {
    handlers.onOptions([]);
    handlers.onLoading(false);
    handlers.onError(null);
    return () => undefined;
  }
  const controller = new AbortController();
  handlers.onLoading(true);
  const timer = setTimeout(async () => {
    try {
      const options = await lookup(accountId, query.trim(), controller.signal);
      if (controller.signal.aborted) return;
      handlers.onOptions(options);
      handlers.onError(null);
    } catch (error) {
      if (controller.signal.aborted) return;
      handlers.onOptions([]);
      handlers.onError(geoErrorKind(error as { status?: number; code?: string }));
    } finally {
      if (!controller.signal.aborted) handlers.onLoading(false);
    }
  }, delay);
  return () => {
    clearTimeout(timer);
    controller.abort();
  };
};
